import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ToastViewport, type ToastType } from "../components/Toast";
import { createPortal } from "react-dom";

export interface ToastOptions {
  /** Milliseconds until dismissal. Omit or use 0 to keep the toast until dismissed. */
  duration?: number;
}

interface ToastEntry {
  id: string;
  type: ToastType;
  message: string;
  expiresAt: number | null;
}

interface ToastApi {
  show(message: string, type?: ToastType, options?: ToastOptions): string;
  success(message: string, options?: ToastOptions): string;
  error(message: string, options?: ToastOptions): string;
  info(message: string, options?: ToastOptions): string;
  warning(message: string, options?: ToastOptions): string;
  dismiss(id: string): void;
  dismissAll(): void;
}

const ToastContext = createContext<ToastApi | null>(null);
const ToastTargetContext = createContext<HTMLElement | null>(null);

/** Controlled notices keep their owning context's lifetime while sharing the stack. */
export function ToastSlot({ children }: { children: ReactNode }) {
  const target = useContext(ToastTargetContext);
  return target ? createPortal(children, target) : null;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const nextId = useRef(0);
  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);
  const dismissAll = useCallback(() => { setToasts([]); }, []);
  const show = useCallback((message: string, type: ToastType = "info", options: ToastOptions = {}) => {
    const duration = options.duration ?? 0;
    if (!Number.isFinite(duration) || duration < 0 || duration > 2_147_483_647) {
      throw new RangeError("Toast duration must be between 0 and 2147483647 milliseconds.");
    }
    const id = `toast-${++nextId.current}`;
    const expiresAt = duration > 0 ? Date.now() + duration : null;
    setToasts((current) => [...current, { id, type, message, expiresAt }]);
    return id;
  }, []);

  useEffect(() => {
    // Retain each original deadline when another toast is added or dismissed.
    const timers = toasts.flatMap((toast) => toast.expiresAt === null ? [] : [
      window.setTimeout(() => dismiss(toast.id), Math.max(0, toast.expiresAt - Date.now())),
    ]);
    return () => { timers.forEach((timer) => window.clearTimeout(timer)); };
  }, [toasts, dismiss]);

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (message, options) => show(message, "success", options),
    error: (message, options) => show(message, "error", options),
    info: (message, options) => show(message, "info", options),
    warning: (message, options) => show(message, "warning", options),
    dismiss,
    dismissAll,
  }), [show, dismiss, dismissAll]);

  return <ToastContext.Provider value={api}>
    <ToastTargetContext.Provider value={target}>
    {children}
    <ToastViewport toasts={toasts} onDismiss={dismiss} targetRef={setTarget} />
    </ToastTargetContext.Provider>
  </ToastContext.Provider>;
}

export function useToast(): ToastApi {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error("useToast must be used within a ToastProvider.");
  return toast;
}
